import React, { useEffect, useState, useCallback, useMemo } from "react";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { api, extractErr } from "@/lib/api";
import { EmptyState, Skeleton } from "@/components/EmptyState";
import { PackagePlus, Upload, Camera, X, ScanLine } from "lucide-react";
import { Combobox } from "@/components/Combobox";
import { useLocation, useNavigate } from "react-router-dom";

export default function GoodsIntake() {
  const navigate = useNavigate();
  const location = useLocation();
  const preselectItemId = useMemo(() => {
    const p = new URLSearchParams(location.search);
    return p.get("item_id") || "";
  }, [location.search]);
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ item_id: "", quantity: 1, supplier: "", note: "", photo_base64: null, lot_number: "", expiry_date: "" });
  const [submitting, setSubmitting] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [inv, mv, sup] = await Promise.all([
        api.get("/inventory?limit=2000"),
        api.get("/goods/movements?type=intake&limit=20"),
        api.get("/suppliers"),
      ]);
      setItems(inv.data.items || []);
      setRecent(mv.data.movements || []);
      setSuppliers(sup.data.suppliers || []);
    } catch (e) { toast.error(extractErr(e)); }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Pre-fill item_id from URL (?item_id=...) and auto-populate supplier from the item
  useEffect(() => {
    if (!preselectItemId || !items.length) return;
    const it = items.find((x) => x.id === preselectItemId);
    if (!it) return;
    setForm((prev) => (prev.item_id === preselectItemId ? prev : {
      ...prev,
      item_id: preselectItemId,
      supplier: prev.supplier || it.supplier || "",
    }));
  }, [preselectItemId, items]);

  // Combobox options
  const itemOptions = useMemo(
    () => items.map((i) => ({ value: i.id, label: i.name, sublabel: `${i.sku} · ${i.quantity} ${i.unit || ""}` })),
    [items]
  );
  const supplierOptions = useMemo(
    () => suppliers.map((s) => ({ value: s.name, label: s.name, sublabel: s.email || s.phone || "" })),
    [suppliers]
  );

  const onPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 800 * 1024) { toast.error("Photo must be <800 KB"); return; }
    const r = new FileReader();
    r.onload = () => setForm((prev) => ({ ...prev, photo_base64: r.result }));
    r.readAsDataURL(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.item_id) { toast.error("Select an item"); return; }
    if (Number(form.quantity) <= 0) { toast.error("Quantity must be > 0"); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        quantity: Number(form.quantity),
        expiry_date: form.expiry_date ? new Date(form.expiry_date).toISOString() : null,
        lot_number: form.lot_number || null,
      };
      await api.post("/goods/intake", payload);
      toast.success("Stock added");
      setForm({ item_id: "", quantity: 1, supplier: "", note: "", photo_base64: null, lot_number: "", expiry_date: "" });
      refresh();
    } catch (e) { toast.error(extractErr(e)); }
    setSubmitting(false);
  };

  return (
    <AppShell title="Goods Intake" subtitle="Receive stock and update quantities"
      headerActions={<Button variant="secondary" size="sm" onClick={() => navigate("/app/scanner")}><ScanLine className="w-4 h-4 mr-1.5" />Scan</Button>}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-2xl border border-border bg-card p-4 sm:p-6" data-testid="goods-intake-form">
          <h3 className="font-heading text-lg font-semibold">Add stock</h3>
          <p className="text-sm text-muted-foreground">Records a stock-in movement and updates on-hand quantity automatically.</p>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
            <div className="space-y-1.5 md:col-span-2">
              <Label>Item *</Label>
              <Combobox
                value={form.item_id}
                onChange={(v) => setForm((p) => ({ ...p, item_id: v }))}
                options={itemOptions}
                placeholder="Type to search inventory or pick from the list…"
                allowCustom={false}
                triggerTestId="intake-item-combobox"
                emptyText="No item matches. Add it on the Inventory page first."
              />
            </div>
            <div className="space-y-1.5">
              <Label>Quantity *</Label>
              <Input type="number" step="any" min="0" value={form.quantity} onChange={(e) => setForm((p) => ({ ...p, quantity: e.target.value }))} data-testid="intake-qty-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Supplier <span className="text-muted-foreground text-[11px]">(type or pick)</span></Label>
              <Combobox
                value={form.supplier}
                onChange={(v) => setForm((p) => ({ ...p, supplier: v }))}
                options={supplierOptions}
                placeholder="Pick a supplier or type a name / PO#…"
                allowCustom={true}
                triggerTestId="intake-supplier-combobox"
              />
            </div>
            <div className="space-y-1.5 md:col-span-2">
              <Label>Note</Label>
              <Textarea value={form.note} onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))} rows={2} placeholder="Received on..." />
            </div>
            <div className="space-y-1.5">
              <Label>Lot / batch # <span className="text-muted-foreground text-[11px]">(optional)</span></Label>
              <Input value={form.lot_number} onChange={(e) => setForm((p) => ({ ...p, lot_number: e.target.value }))} placeholder="LOT-2026-0001" className="font-mono" data-testid="intake-lot-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Expiry date <span className="text-muted-foreground text-[11px]">(optional)</span></Label>
              <Input type="date" value={form.expiry_date} onChange={(e) => setForm((p) => ({ ...p, expiry_date: e.target.value }))} data-testid="intake-expiry-input" />
            </div>
            <div className="md:col-span-2">
              <Label>Delivery photo (optional)</Label>
              <div className="mt-2 flex items-center gap-3">
                {form.photo_base64 ? (
                  <div className="relative">
                    <img src={form.photo_base64} alt="delivery" className="w-24 h-24 rounded-lg object-cover border border-border bg-white" />
                    <button type="button" className="absolute -top-1 -right-1 rounded-full bg-background border border-border p-1" onClick={() => setForm((p) => ({ ...p, photo_base64: null }))}><X className="w-3 h-3" /></button>
                  </div>
                ) : (
                  <div className="w-24 h-24 rounded-lg bg-muted flex items-center justify-center text-muted-foreground"><Camera className="w-5 h-5" /></div>
                )}
                <label className="flex items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-sm cursor-pointer hover:bg-accent/50">
                  <Upload className="w-4 h-4" /> Upload / take photo
                  <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onPhoto} data-testid="intake-photo-input" />
                </label>
              </div>
            </div>
            <div className="md:col-span-2 flex justify-end gap-2 pt-2 border-t border-border mt-2">
              <Button type="submit" disabled={submitting} data-testid="intake-submit-button"><PackagePlus className="w-4 h-4 mr-1.5" />{submitting ? "Saving…" : "Record intake"}</Button>
            </div>
          </form>
        </div>

        <div className="rounded-2xl border border-border bg-card p-4">
          <h3 className="font-heading font-semibold">Recent intakes</h3>
          <div className="mt-3 space-y-2 max-h-[560px] overflow-y-auto">
            {loading ? [...Array(5)].map((_, i) => <Skeleton key={i} className="h-14" />)
              : recent.length === 0 ? <EmptyState className="py-6 border-0" title="No recent intakes" description="Intakes will appear here." />
              : recent.map((m) => (
                  <div key={m.id} className="rounded-lg border border-border p-2.5 text-sm">
                    <div className="flex items-center justify-between">
                      <div className="font-medium truncate">{m.item_name}</div>
                      <div className="text-emerald-600 dark:text-emerald-400 font-medium">+{m.quantity}</div>
                    </div>
                    <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                      <span className="font-mono">{m.item_sku}</span>
                      <span>{new Date(m.timestamp).toLocaleString()}</span>
                    </div>
                    {m.supplier && <div className="text-[11px] text-muted-foreground mt-1">Supplier: {m.supplier}</div>}
                    {m.photo_base64 && <img src={m.photo_base64} alt="" className="mt-1 w-full max-h-32 object-cover rounded border border-border" />}
                  </div>
                ))}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
